import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import services from '../data/services';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import '../styles/pages/services.css';

const DEMO_URL = 'https://calendar.app.google/6g62S4xtRa8eWipt5';

/* Hero capability tiles */
const HERO_TILES = [
  { icon: 'fa-brands fa-microsoft', label: 'Power Platform', depth: '0.06' },
  { icon: 'fa-solid fa-brain', label: 'AI Research', depth: '-0.08', note: true },
  { icon: 'fa-solid fa-notes-medical', label: 'Healthcare', depth: '0.1' },
  { icon: 'fa-solid fa-cloud', label: 'Cloud & DevOps', depth: '-0.04' },
];

/* Our expertise */
const EXPERTISE = [
  {
    icon: 'fa-brands fa-microsoft',
    title: 'Power Platform',
    text: 'Enterprise-grade low-code solutions with advanced security, compliance, and integration capabilities.',
    items: ['Custom Power Apps development', 'Dataverse optimization', 'Power Automate workflows', 'Role-based security & masking'],
  },
  {
    icon: 'fa-solid fa-brain',
    title: 'AI Research & Deployment',
    text: 'Cutting-edge AI solutions from research to production with scalable architecture and monitoring.',
    items: ['Custom LLM fine-tuning', 'Federated learning systems', 'Predictive analytics models', 'Production ML pipelines'],
  },
  {
    icon: 'fa-solid fa-notes-medical',
    title: 'Healthcare Integration',
    text: 'HIPAA-compliant data integration with HL7/FHIR standards and seamless EHR connectivity.',
    items: ['HL7/FHIR message processing', 'EHR system integrations', 'HIPAA & PIPEDA compliance', 'Clinical data standardization'],
  },
  {
    icon: 'fa-solid fa-universal-access',
    title: 'Compliance & Accessibility',
    text: 'WCAG 2.1 AA compliance audits with comprehensive privacy impact assessments and governance.',
    items: ['WCAG 2.1 AA compliance', 'Privacy impact assessments', 'Data governance frameworks', 'Accessibility remediation'],
  },
  {
    icon: 'fa-solid fa-diagram-project',
    title: 'Strategy & Transformation',
    text: 'Digital transformation roadmaps with ROI analysis and change management for sustainable growth.',
    items: ['Process automation assessment', 'Digital transformation roadmaps', 'ROI analysis & measurement', 'Change management support'],
  },
  {
    icon: 'fa-solid fa-cloud',
    title: 'Cloud & DevOps',
    text: 'Scalable cloud architecture with automated CI/CD pipelines and comprehensive monitoring solutions.',
    items: ['Cloud architecture design', 'CI/CD pipeline automation', 'Infrastructure as Code', 'Performance monitoring'],
  },
];

const TECH = [
  { icon: 'fa-brands fa-microsoft', name: 'Microsoft', sub: 'Azure & Power Platform' },
  { icon: 'fa-brands fa-aws', name: 'AWS', sub: 'Cloud Infrastructure' },
  { icon: 'fa-brands fa-python', name: 'Python', sub: 'AI & Analytics' },
  { icon: 'fa-solid fa-robot', name: 'OpenAI', sub: 'Language Models' },
];

/* Ready-to-deploy solutions */
const SOLUTIONS = [
  {
    icon: 'fa-headset',
    name: 'Digital Front Desk',
    kicker: '24/7 AI Receptionist',
    text: 'Intelligent voice assistant that handles calls, schedules appointments, and manages visitor intake for healthcare, property management, and public services.',
    features: ['AI-powered voice assistant', 'Smart appointment scheduling', 'SMS & chat integration', 'Visitor management system'],
    audience: ['Healthcare', 'Property Mgmt', 'Public Services'],
    primary: { href: 'https://digitalfrontdesk.ca/', label: 'Visit Website', external: true },
    demoLabel: 'Book Demo',
  },
  {
    icon: 'fa-screwdriver-wrench',
    name: 'TextGenetic',
    kicker: 'Predictive Maintenance',
    text: 'Advanced analytics platform that predicts equipment failures and optimizes fleet health through real-time monitoring and machine learning.',
    features: ['Predictive analytics engine', 'Real-time fleet monitoring', 'Automated maintenance scheduling', 'Cost optimization insights'],
    audience: ['Fleet Management', 'Manufacturing', 'Logistics'],
    primary: { href: 'https://textgenetic.com', label: 'Visit Website', external: true },
    demoLabel: 'Book Demo',
  },
  {
    icon: 'fa-file-waveform',
    name: 'My Clinical Writer',
    kicker: 'AI Documentation',
    text: 'Privacy-first AI assistant for mental health professionals that streamlines SOAP notes and clinical documentation while maintaining patient confidentiality.',
    features: ['AI-powered SOAP notes', 'Automated report generation', 'Privacy-first design', 'Template customization'],
    audience: ['Psychology', 'Mental Health', 'Therapy'],
    primary: { href: 'https://myclinicalwriter.ai', label: 'Visit Website', external: true },
    demoLabel: 'Book Demo',
  },
  {
    icon: services.cryptocurrency.icon,
    name: services.cryptocurrency.shortTitle,
    kicker: 'Blockchain Token Economy',
    text: services.cryptocurrency.shortDescription,
    features: ['70-90% cost reduction', 'Gamification rewards', 'Blockchain security', 'Analytics dashboard'],
    audience: ['E-learning', 'E-commerce', 'Gaming'],
    primary: { href: `/services/${services.cryptocurrency.id}`, label: 'Learn More', external: false },
    demoLabel: 'Book a Demo',
  },
];

const CUSTOM_BUILDS = [
  { icon: 'fa-gears', label: 'Power Platform Apps' },
  { icon: 'fa-comments', label: 'AI Assistants' },
  { icon: 'fa-stethoscope', label: 'Healthcare Systems' },
  { icon: 'fa-chart-line', label: 'Analytics Platforms' },
];

const PHASES = ['Discover', 'Design', 'Build', 'Deploy'];

const OUTCOMES = [
  {
    sector: 'Healthcare',
    quote: 'Our staff can now focus on patient care instead of answering repetitive calls. The AI voice assistant has transformed how we handle client communications.',
    metric: '95% Call Response Rate',
    org: 'Clinical Psychology Practice',
  },
  {
    sector: 'Technology',
    quote: 'The automation solution has allowed us to scale our operations without proportionally increasing our administrative overhead.',
    metric: '80% Scheduling Automation',
    org: 'Managed ISP & Network Services',
  },
  {
    sector: 'Financial Services',
    quote: "I can now serve clients in their preferred language while automating my entire intake process. It's given me confidence in managing sensitive information.",
    metric: '80% Faster Intake',
    org: 'Financial Advisory Practice',
  },
];

const CTA_STATS = [
  { n: '15', label: 'Years Combined Experience' },
  { n: '50', label: 'Successful Projects' },
  { n: '20', label: 'Hours Saved Weekly' },
];

/* Pointer-follow spotlight on solution cards (CSS vars only; no global listeners). */
function trackSpotlight(e) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty('--mx', `${e.clientX - r.left}px`);
  el.style.setProperty('--my', `${e.clientY - r.top}px`);
}

function Services() {
  const { pathname } = useLocation();
  const pageLabel = pathname.startsWith('/solutions') ? 'Solutions' : 'Services';

  return (
    <div className="services-page">
      {/* HERO */}
      <PageHero
        variant="dark"
        className="svc-hero"
        eyebrow="Enterprise-Grade Automation"
        crumbs={[{ to: '/', label: 'Home' }, { label: pageLabel }]}
        title={<>Services &amp; Solutions That <em>Scale</em></>}
        lead="From AI research to production deployment, we bridge the gap between advanced technology and real business results. Transform your operations with PhD-level expertise."
        actions={<>
          <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
            <i className="fa-solid fa-calendar-check" aria-hidden="true" /> Book a Demo
          </a>
          <a href="#solutions" className="btn btn-outline-light btn-lg">
            Explore Solutions <i className="fa-solid fa-arrow-down" aria-hidden="true" />
          </a>
        </>}
      >
        <div className="svc-hero__board">
          <ul className="svc-hero__tiles" aria-label="Core capabilities">
            {HERO_TILES.map((t, i) => (
              <li key={t.label} className="svc-hero__slot" data-parallax={t.depth}>
                <div className={`svc-tile${t.note ? ' svc-tile--note' : ''}`} style={{ '--tilt': `${[-2.5, 2, 1.5, -1.8][i]}deg` }}>
                  <i className={t.icon} aria-hidden="true" />
                  <span>{t.label}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="svc-stamp" aria-hidden="true">
            <svg viewBox="0 0 120 120" className="svc-stamp__ring">
              <defs><path id="svc-stamp-path" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
              <text><textPath href="#svc-stamp-path">From Prototype to Production • From Prototype to Production •</textPath></text>
            </svg>
            <i className="fa-solid fa-rocket" />
          </div>
        </div>
      </PageHero>

      {/* EXPERTISE */}
      <section className="section section--light" aria-labelledby="svc-expertise-title">
        <div className="container">
          <SectionHeader
            index="01"
            eyebrow="From Prototype to Production"
            title={<>Our <em>Expertise</em></>}
            lead="Comprehensive technical services backed by PhD-level research and real-world experience"
            id="svc-expertise-title"
          />

          <div className="stats svc-stats" data-reveal-stagger>
            <div className="stat">
              <p className="stat__num"><span data-count="6">6</span></p>
              <p className="stat__label">Core Services</p>
            </div>
            <div className="stat">
              <p className="stat__num"><span data-count="4">4</span></p>
              <p className="stat__label">Ready Solutions</p>
            </div>
            <div className="stat">
              <p className="stat__num"><span className="stat__suffix">∞</span></p>
              <p className="stat__label">Custom Possibilities</p>
            </div>
          </div>

          <div className="grid-3 svc-grid" data-reveal-stagger="0.1">
            {EXPERTISE.map((s, i) => (
              <article key={s.title} className="card card--feature card--num svc-card" data-num={String(i + 1).padStart(2, '0')}>
                <span className="icon-tile"><i className={s.icon} aria-hidden="true" /></span>
                <h3 className="card__title">{s.title}</h3>
                <p className="card__text">{s.text}</p>
                <ul className="ticks svc-card__ticks">
                  {s.items.map((it) => <li key={it}>{it}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERTISE MARQUEE */}
      <section className="section section--dark section--flush svc-marquee-band" aria-label="Service areas">
        <div className="marquee marquee--words marquee--fade" data-marquee data-speed="55">
          <div className="marquee__track">
            {EXPERTISE.map((s, i) => (
              <span className="marquee__item" key={s.title}>
                {i % 2 ? <em>{s.title}</em> : s.title}
                <span className="marquee__sep" aria-hidden="true" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGY */}
      <section className="section section--sand" aria-labelledby="svc-tech-title">
        <div className="container">
          <div className="feature-split">
            <div>
              <SectionHeader
                align="stack"
                index="02"
                eyebrow="Technology"
                title={<>Powered by Enterprise <em>Technology</em></>}
                lead="We leverage the most advanced, secure, and scalable technologies to deliver solutions that grow with your business."
                id="svc-tech-title"
              />
              <ul className="svc-tech" data-reveal-stagger>
                {TECH.map((t) => (
                  <li key={t.name} className="svc-tech__item">
                    <span className="icon-tile icon-tile--ink"><i className={t.icon} aria-hidden="true" /></span>
                    <span>
                      <strong>{t.name}</strong>
                      <small>{t.sub}</small>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="svc-tech__media">
              <div className="frame frame--4x5 svc-tech__frame" data-reveal-img>
                <img
                  src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1600&auto=format&fit=crop"
                  alt="Technology stack"
                  loading="lazy"
                  data-parallax="0.08"
                />
              </div>
              <div className="card card--flat svc-secure" data-reveal="up" data-delay="0.3">
                <span className="icon-tile icon-tile--note"><i className="fa-solid fa-shield-halved" aria-hidden="true" /></span>
                <div>
                  <h3 className="card__title">Enterprise-Grade Security</h3>
                  <p className="card__text">Canadian data residency, SOC 2 compliance, and enterprise-grade security by default.</p>
                  <div className="cluster svc-secure__pills">
                    <span className="badge-soft"><i className="fa-brands fa-canadian-maple-leaf" aria-hidden="true" /> Canadian Hosted</span>
                    <span className="badge-soft"><i className="fa-solid fa-lock" aria-hidden="true" /> SOC 2 Compliant</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FLAGSHIP SOLUTIONS */}
      <section id="solutions" className="section section--dark grain" aria-labelledby="svc-solutions-title">
        <div className="container">
          <SectionHeader
            index="03"
            eyebrow={<><span className="dot-live svc-live" aria-hidden="true" />Live &amp; Scaling</>}
            title={<>Ready-to-Deploy <em>Solutions</em></>}
            lead="Proven products that have saved thousands of hours for our clients"
            id="svc-solutions-title"
          />

          <div className="grid-2 svc-solutions" data-reveal-stagger="0.12">
            {SOLUTIONS.map((p, i) => (
              <article key={p.name} className="card card--dark svc-sol" onPointerMove={trackSpotlight}>
                <header className="svc-sol__head">
                  <span className="svc-sol__icon"><i className={`fa-solid ${p.icon}`} aria-hidden="true" /></span>
                  <div>
                    <p className="card__kicker">{p.kicker}</p>
                    <h3 className="card__title">{p.name}</h3>
                  </div>
                  <span className="svc-sol__index" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                </header>
                <p className="card__text">{p.text}</p>

                <div className="svc-sol__block">
                  <h4 className="svc-sol__label">Key Features</h4>
                  <ul className="ticks svc-sol__ticks">
                    {p.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </div>

                <div className="svc-sol__block">
                  <h4 className="svc-sol__label">Perfect For</h4>
                  <div className="cluster svc-sol__tags">
                    {p.audience.map((a) => <span key={a} className="badge-soft">{a}</span>)}
                  </div>
                </div>

                <div className="svc-sol__foot">
                  {p.primary.external ? (
                    <a className="btn btn-light" href={p.primary.href} target="_blank" rel="noopener noreferrer">
                      {p.primary.label} <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                    </a>
                  ) : (
                    <Link className="btn btn-light" to={p.primary.href}>
                      {p.primary.label} <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                    </Link>
                  )}
                  <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="btn btn-outline-light">
                    {p.demoLabel}
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOM SOLUTIONS */}
      <section className="section section--light" aria-labelledby="svc-custom-title">
        <div className="container">
          <div className="feature-split feature-split--reverse svc-custom">
            <div>
              <SectionHeader
                align="stack"
                index="04"
                eyebrow="Custom builds"
                title={<>Beyond <em>Off-the-Shelf</em></>}
                lead="When your vision requires something unique, our team creates custom solutions that perfectly align with your business goals and technical requirements."
                id="svc-custom-title"
              />
              <ul className="svc-builds" data-reveal-stagger>
                {CUSTOM_BUILDS.map((b) => (
                  <li key={b.label} className="svc-builds__item">
                    <span className="icon-tile icon-tile--sm"><i className={`fa-solid ${b.icon}`} aria-hidden="true" /></span>
                    {b.label}
                  </li>
                ))}
              </ul>
              <div className="cluster svc-custom__actions" data-reveal>
                <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
                  Discuss Your Vision
                </a>
                <Link to="/case-studies" className="btn btn-ghost btn-lg">
                  See Examples <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="svc-custom__media">
              <div className="frame frame--4x3" data-reveal-img>
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1600&auto=format&fit=crop"
                  alt="Custom development"
                  loading="lazy"
                  data-parallax="0.07"
                />
                <span className="frame__caption pill pill--glass"><i className="fa-solid fa-pen-ruler" aria-hidden="true" /> Custom Build</span>
              </div>
              <div className="card card--flat svc-method" data-reveal="up" data-delay="0.2">
                <h3 className="card__title">Tailored to Your Vision</h3>
                <p className="card__text">Every custom solution follows our practical 4-phase methodology for delivery confidence.</p>
                <ol className="svc-phases">
                  {PHASES.map((ph, i) => (
                    <li key={ph}>
                      <span className="svc-phases__num">{i + 1}</span>
                      <span className="svc-phases__label">{ph}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT OUTCOMES */}
      <section className="section section--sand" aria-labelledby="svc-outcomes-title">
        <div className="container">
          <SectionHeader
            index="05"
            eyebrow="Client outcomes"
            title={<>Client <em>outcomes</em></>}
            lead="Examples from organizations using our delivery model"
            id="svc-outcomes-title"
          />
          <div className="grid-3 svc-quotes" data-reveal-stagger="0.1">
            {OUTCOMES.map((o) => (
              <figure key={o.org} className="card svc-quote">
                <span className="badge-soft">{o.sector}</span>
                <blockquote className="svc-quote__text">
                  <p>&ldquo;{o.quote}&rdquo;</p>
                </blockquote>
                <figcaption className="svc-quote__foot">
                  <span className="pill pill--note">{o.metric}</span>
                  <span className="svc-quote__org">{o.org}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING (light; the global footer follows with the dark CTA band) */}
      <section className="section section--light svc-close" aria-labelledby="svc-close-title">
        <div className="container">
          <div className="svc-close__grid">
            <div>
              <p className="eyebrow" data-reveal><span className="eyebrow__num">06</span>Get started</p>
              <h2 className="h1 svc-close__title" id="svc-close-title" data-split>Start Your <em>Transformation</em></h2>
              <p className="lead svc-close__lead" data-reveal data-delay="0.15">
                Whether you need our expertise, one of our practical solutions, or a completely custom build — we're here to turn your vision into reality.
              </p>
              <div className="stats svc-close__stats" data-reveal-stagger>
                {CTA_STATS.map((s) => (
                  <div className="stat" key={s.label}>
                    <p className="stat__num"><span data-count={s.n}>{s.n}</span><span className="stat__suffix">+</span></p>
                    <p className="stat__label">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <aside className="card card--note card--flat svc-close__card" data-reveal="right" data-delay="0.2" aria-label="Book a free strategy call">
              <p className="card__text svc-close__intro">Book a free strategy call and discover how AI automation can improve your operations.</p>
              <div className="svc-close__actions">
                <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="btn btn-ink btn-lg" data-magnetic>
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" /> Book a Demo
                </a>
                <Link to="/case-studies" className="btn btn-ghost">
                  View Success Stories <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </Link>
              </div>
              <ul className="ticks svc-close__ticks">
                <li>Free 30-minute consultation</li>
                <li>No commitment required</li>
                <li>Canadian data residency</li>
              </ul>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;
